import { memo, useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react'
import { useGameStore } from '@/store/gameStore'
import { useRangeMatrix } from '@/hooks/useRangeMatrix'
import { ChartWithHeatmap } from './ChartWithHeatmap'
import { StrikeMatrix } from './StrikeMatrix'
import { ChartAlignedMatrix } from './ChartAlignedMatrix'
import { OrderPlacementDialog } from './OrderPlacementDialog'
import { ChartApiProvider } from '@/contexts/ChartApiContext'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { hapticLight } from '@/telegram'
import { useSubscription } from '@/hooks/useSubscription'
import { RenewalModal } from './RenewalModal'

import { useQueryClient } from '@tanstack/react-query'
import { parsePriceRange, type MatrixCell } from '@/lib/rangeMarketUtils'

/** Возвращает UTC-смещение America/New_York в минутах (-240 EDT, -300 EST) */
function getEtOffsetMinutes(date: Date): number {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: 'America/New_York',
      timeZoneName: 'shortOffset',
    }).formatToParts(date)
    const tz = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT-5'
    const m = tz.match(/GMT([+-]\d+)(?::(\d+))?/)
    if (!m) return -5 * 60
    const h = parseInt(m[1])
    const mins = m[2] ? parseInt(m[2]) : 0
    return h * 60 + (h < 0 ? -mins : mins)
  } catch {
    return -5 * 60
  }
}

function getOverlayFraction(): number {
  const now = new Date()
  const etOffsetMin = getEtOffsetMinutes(now)
  const etNow = new Date(now.getTime() + (etOffsetMin - now.getTimezoneOffset()) * 60 * 1000)
  const dayStart = new Date(etNow)
  dayStart.setHours(12, 0, 0, 0)
  const dayEnd = new Date(dayStart)
  dayEnd.setDate(dayEnd.getDate() + 1)
  if (etNow < dayStart) return 0
  if (etNow > dayEnd) return 1
  return (etNow.getTime() - dayStart.getTime()) / (dayEnd.getTime() - dayStart.getTime())
}

export const MainTradingView = memo(function MainTradingView() {
  const { currentCrypto } = useGameStore()
  const { matrix, endDates, baseDayOffset, isLoading, error } = useRangeMatrix(currentCrypto)
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const isPmUnlocked = usePmWalletStore((s) => s.pmAddress) !== null
  const { isActive: subActive } = useSubscription()
  const canTrade = activeWallet === 'pm' && isPmUnlocked && subActive
  const [showExpiredBlocker, setShowExpiredBlocker] = useState(false)
  const currentCryptoRef = useRef(currentCrypto)
  currentCryptoRef.current = currentCrypto
  const [overlayFraction, setOverlayFraction] = useState(getOverlayFraction)
  const [chartApi, setChartApi] = useState<{
    chart: import('lightweight-charts').IChartApi
    series: import('lightweight-charts').ISeriesApi<'Candlestick'> | import('lightweight-charts').ISeriesApi<'Line'>
  } | null>(null)
  const [lastCandleTime, setLastCandleTime] = useState<number | null>(null)
  const dotRef = useRef<HTMLDivElement>(null)
  const fittedSpanRef = useRef<number>(0) // natural span after fitContent, before any compression
  const prevBaseDayOffsetRef = useRef<number>(0)
  const qc = useQueryClient()
  const [dialogCell, setDialogCell] = useState<MatrixCell | null>(null)
  const [dialogSide, setDialogSide] = useState<'YES' | 'NO'>('YES')
  const [selectedCellIds, setSelectedCellIds] = useState<Set<string>>(() => new Set())

  // Selected cells resolved from matrix
  const selectedCells = useMemo(() => {
    if (!selectedCellIds.size) return []
    const found: MatrixCell[] = []
    const seen = new Set<string>()
    for (const row of matrix) {
      for (const cell of row) {
        if (cell && selectedCellIds.has(cell.id) && !seen.has(cell.id)) {
          found.push(cell)
          seen.add(cell.id)
        }
      }
    }
    return found
  }, [selectedCellIds, matrix])
  void selectedCells

  const toggleCellSelection = useCallback((cellId: string) => {
    setSelectedCellIds((prev) => {
      const next = new Set(prev)
      if (next.has(cellId)) next.delete(cellId)
      else next.add(cellId)
      return next
    })
  }, [])

  const matrixRef = useRef<MatrixCell[][]>([])
  matrixRef.current = matrix

  const chartApiLatestRef = useRef<typeof chartApi>(null)
  chartApiLatestRef.current = chartApi

  /** Горизонтальная рецентровка — использует fittedSpan / 2 (идемпотентно) */
  const centerTimeAxisOnly = useCallback((chart: import('lightweight-charts').IChartApi) => {
    const timeScale = chart.timeScale()
    const logicalRange = timeScale.getVisibleLogicalRange()
    if (!logicalRange) return
    const span = fittedSpanRef.current > 0 ? fittedSpanRef.current * 2 : (logicalRange.to - logicalRange.from) * 2
    const rightOffset = timeScale.options().rightOffset ?? 48
    const lastBarLogical = logicalRange.to - rightOffset
    timeScale.setVisibleLogicalRange({ from: lastBarLogical - span / 2, to: lastBarLogical + span / 2 })
  }, [])

  /** Полная центровка: X = fittedSpan / 2 (фикс), Y = полный диапазон матрицы (спек 4.4) */
  const centerChartOnNow = useCallback((chart: import('lightweight-charts').IChartApi) => {
    const timeScale = chart.timeScale()
    const logicalRange = timeScale.getVisibleLogicalRange()
    if (!logicalRange) return
    const span = fittedSpanRef.current > 0 ? fittedSpanRef.current * 2 : (logicalRange.to - logicalRange.from) * 2
    const rightOffset = timeScale.options().rightOffset ?? 48
    const lastBarLogical = logicalRange.to - rightOffset
    // Shift content left 15%: move visible range right so content appears on left
    const xShift = span * 0.15
    timeScale.setVisibleLogicalRange({ from: lastBarLogical - span / 2 + xShift, to: lastBarLogical + span / 2 + xShift })

    // Спек 4.4: Y-ось = от нижнего до верхнего ценового диапазона матрицы (все ряды)
    const m = matrixRef.current
    if (m.length) {
      let minPrice = Infinity
      let maxPrice = -Infinity
      let stepSum = 0
      let stepCount = 0
      let hasEdgeLow = false   // <X row (low=0)
      let hasEdgeHigh = false  // >X row (high=Infinity)
      let edgeLowHigh = 0     // upper bound of <X row
      let edgeHighLow = 0     // lower bound of >X row
      for (const row of m) {
        const cell = row[0]
        if (!cell) continue
        const range = parsePriceRange(cell.rangeLabel)
        if (!range) continue
        if (range.low === 0) {
          hasEdgeLow = true
          edgeLowHigh = range.high
        } else if (range.high === Infinity) {
          hasEdgeHigh = true
          edgeHighLow = range.low
        } else {
          if (range.low < minPrice) minPrice = range.low
          if (range.high > maxPrice) maxPrice = range.high
          stepSum += range.high - range.low
          stepCount++
        }
      }
      const step = stepCount > 0 ? stepSum / stepCount : 0
      // Extend range to include edge rows (one step beyond their boundary)
      if (hasEdgeLow) {
        const edgeMin = edgeLowHigh - step
        if (edgeMin < minPrice) minPrice = edgeMin
      }
      if (hasEdgeHigh) {
        const edgeMax = edgeHighLow + step
        if (edgeMax > maxPrice) maxPrice = edgeMax
      }
      if (minPrice < Infinity && maxPrice > -Infinity) {
        // Stretch vertically 1.5x: reduce visible span, shift up 15%
        const priceRange = maxPrice - minPrice
        const yShift = priceRange * 0.15
        const center = (minPrice + maxPrice) / 2 - yShift
        const halfSpan = priceRange / 1.5 / 2
        chart.priceScale('left').applyOptions({ autoScale: false })
        chart.priceScale('left').setVisibleRange({ from: center - halfSpan, to: center + halfSpan })
      }
    }
  }, [])

  const onChartReady = useCallback(
    (chart: import('lightweight-charts').IChartApi, series: import('lightweight-charts').ISeriesApi<'Candlestick'> | import('lightweight-charts').ISeriesApi<'Line'>) => {
      setChartApi({ chart, series })
    },
    []
  )
  const onChartFitted = useCallback(
    (chart: import('lightweight-charts').IChartApi) => {
      const lr = chart.timeScale().getVisibleLogicalRange()
      if (lr) fittedSpanRef.current = lr.to - lr.from
      centerChartOnNow(chart)
    },
    [centerChartOnNow]
  )
  const onChartDestroy = useCallback(() => {
    setChartApi(null)
    setLastCandleTime(null)
  }, [])
  useEffect(() => {
    const i = setInterval(() => setOverlayFraction(getOverlayFraction()), 60_000)
    return () => clearInterval(i)
  }, [])

  // При сдвиге колонок (истечение первой) — центрировать «сейчас»
  useLayoutEffect(() => {
    const chart = chartApi?.chart
    const justShifted = baseDayOffset > 0 && prevBaseDayOffsetRef.current === 0
    prevBaseDayOffsetRef.current = baseDayOffset
    if (!chart || !justShifted) return
    centerChartOnNow(chart)
  }, [baseDayOffset, chartApi?.chart, currentCrypto, centerChartOnNow])

  // Re-center Y-axis when matrix structure changes (new crypto matrix loaded).
  const matrixStructureKey = matrix[0]?.[0]?.rangeLabel ?? ''
  useLayoutEffect(() => {
    const chart = chartApi?.chart
    if (!chart || !matrix.length) return
    centerChartOnNow(chart)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixStructureKey, centerChartOnNow])

  const onCellTap = useCallback((cell: MatrixCell, side?: 'YES' | 'NO') => {
    hapticLight()
    if (activeWallet === 'pm' && isPmUnlocked && !subActive) {
      setShowExpiredBlocker(true)
      return
    }
    void qc.invalidateQueries({ queryKey: ['clobMidPrices'] })
    setDialogCell(cell)
    setDialogSide(side ?? 'YES')
  }, [qc, activeWallet, isPmUnlocked, subActive])

  // Рецентровка и масштаб вертикальной оси при повороте экрана
  const containerRef = useRef<HTMLDivElement>(null)
  const portraitPriceRangeRef = useRef<{ from: number; to: number } | null>(null)
  const wasLandscapeRef = useRef<boolean>(typeof window !== 'undefined' && window.innerWidth > window.innerHeight)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    let lastWidth = el.clientWidth
    let rafId: number | undefined
    const ro = new ResizeObserver(() => {
      const newWidth = el.clientWidth
      if (newWidth === lastWidth) return
      lastWidth = newWidth
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = undefined
        const chart = chartApiLatestRef.current?.chart
        if (!chart) return

        const isLandscape = window.innerWidth > window.innerHeight
        const wasLandscape = wasLandscapeRef.current
        const priceScale = chart.priceScale('left')

        if (isLandscape && !wasLandscape) {
          const range = priceScale.getVisibleRange()
          if (range) {
            portraitPriceRangeRef.current = { from: range.from as number, to: range.to as number }
            const mid = ((range.from as number) + (range.to as number)) / 2
            const halfSpan = ((range.to as number) - (range.from as number)) / 2
            priceScale.setVisibleRange({ from: mid - halfSpan / 3, to: mid + halfSpan / 3 })
          }
        } else if (!isLandscape && wasLandscape) {
          const saved = portraitPriceRangeRef.current
          if (saved) priceScale.setVisibleRange(saved)
        }

        wasLandscapeRef.current = isLandscape

        centerTimeAxisOnly(chart)
      })
    })
    ro.observe(el)
    return () => { ro.disconnect(); if (rafId !== undefined) cancelAnimationFrame(rafId) }
  }, [centerTimeAxisOnly])

  const linePosition = 50 + overlayFraction * (50 / 3)

  const numCols = matrix[0]?.length ?? 0
  const colIsUnique = useMemo(() => {
    const flags = Array.from({ length: numCols }, () => true)
    if (!matrix.length) return flags
    for (let ci = 1; ci < numCols; ci++) {
      const allSame = matrix.every((row) => {
        const c0 = row[0]
        const cN = row[ci]
        return c0 && cN && c0.yesTokenId === cN.yesTokenId
      })
      if (allSame) flags[ci] = false
    }
    return flags
  }, [matrix, numCols])

  const arbSums = useMemo(() => {
    const sums: number[] = Array.from({ length: numCols }, () => 0)
    if (!selectedCellIds.size) return sums
    for (const row of matrix) {
      for (let ci = 0; ci < row.length; ci++) {
        if (!colIsUnique[ci]) continue
        const cell = row[ci]
        if (cell && selectedCellIds.has(cell.id)) {
          sums[ci] += cell.yesPrice
        }
      }
    }
    return sums
  }, [matrix, selectedCellIds, numCols, colIsUnique])

  const positionOrPriceSums = useMemo(() => {
    const sums: number[] = Array.from({ length: numCols }, () => 0)
    if (!selectedCellIds.size) return sums
    for (const row of matrix) {
      for (let ci = 0; ci < row.length; ci++) {
        if (!colIsUnique[ci]) continue
        const cell = row[ci]
        if (cell && selectedCellIds.has(cell.id)) {
          if (cell.positionInfo && cell.positionInfo.size > 0 && cell.positionInfo.avgPrice > 0) {
            sums[ci] += cell.positionInfo.avgPrice
          } else {
            sums[ci] += cell.yesPrice
          }
        }
      }
    }
    return sums
  }, [matrix, selectedCellIds, numCols, colIsUnique])

  const arbSumsNo = useMemo(() => {
    const sums: number[] = Array.from({ length: numCols }, () => 0)
    if (!selectedCellIds.size) return sums
    for (const row of matrix) {
      for (let ci = 0; ci < row.length; ci++) {
        if (!colIsUnique[ci]) continue
        const cell = row[ci]
        if (cell && selectedCellIds.has(cell.id)) {
          sums[ci] += cell.noPrice
        }
      }
    }
    return sums
  }, [matrix, selectedCellIds, numCols, colIsUnique])

  const positionOrPriceSumsNo = useMemo(() => {
    const sums: number[] = Array.from({ length: numCols }, () => 0)
    if (!selectedCellIds.size) return sums
    for (const row of matrix) {
      for (let ci = 0; ci < row.length; ci++) {
        if (!colIsUnique[ci]) continue
        const cell = row[ci]
        if (cell && selectedCellIds.has(cell.id)) {
          if (cell.positionInfoNo && cell.positionInfoNo.size > 0 && cell.positionInfoNo.avgPrice > 0) {
            sums[ci] += cell.positionInfoNo.avgPrice
          } else {
            sums[ci] += cell.noPrice
          }
        }
      }
    }
    return sums
  }, [matrix, selectedCellIds, numCols, colIsUnique])

  const hasSelection = selectedCellIds.size > 0

  return (
    <ChartApiProvider value={{ chart: chartApi?.chart ?? null, series: chartApi?.series ?? null, lastCandleTime }}>
      <div ref={containerRef} className="relative flex min-h-0 flex-1 min-w-0">
        <div className="absolute inset-0 z-0 min-w-0">
          <ChartWithHeatmap
            rightBarOffset={24}
            dotRef={dotRef}
            onChartReady={onChartReady}
            onChartFitted={onChartFitted}
            onChartDestroy={onChartDestroy}
            onLastCandleTime={setLastCandleTime}
          />
        </div>
        <div
          className="absolute inset-0 z-10 min-w-0"
          style={{
            pointerEvents: chartApi ? 'none' : 'auto',
          }}
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center p-4">
              <span className="text-[#94a3b8]">Загрузка...</span>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center p-4 text-[#ef4444]">
              {error.message}
            </div>
          ) : !matrix.length ? (
            <div className="flex h-full items-center justify-center p-4 text-[#94a3b8]">
              Нет данных для матрицы по выбранной паре
            </div>
          ) : chartApi ? (
            <ChartAlignedMatrix
              matrix={matrix}
              endDates={endDates}
              disabled={!canTrade}
              onCellTap={onCellTap}
              selectedCellIds={selectedCellIds}
              onToggleSelection={toggleCellSelection}
              arbSums={hasSelection ? arbSums : undefined}
              positionOrPriceSums={hasSelection ? positionOrPriceSums : undefined}
              arbSumsNo={hasSelection ? arbSumsNo : undefined}
              positionOrPriceSumsNo={hasSelection ? positionOrPriceSumsNo : undefined}
              containerPageRect={containerRef.current ? (() => { const r = containerRef.current!.getBoundingClientRect(); return { left: r.left, top: r.top } })() : undefined}
            />
          ) : (
            <StrikeMatrix
              matrix={matrix}
              disabled={!canTrade}
              onCellTap={onCellTap}
              selectedCellIds={selectedCellIds}
              onToggleSelection={toggleCellSelection}
            />
          )}
        </div>
        <div
          ref={dotRef}
          className="pointer-events-none absolute z-30"
          style={{
            display: 'none',
            width: 5,
            height: 5,
            marginLeft: -2.5,
            marginTop: -2.5,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #ffffff 0%, #ffffff 40%, rgba(0,212,255,0.6) 70%, transparent 100%)',
            boxShadow: 'inset 0 0 2px 1px rgba(255,255,255,0.9), 0 0 6px 4px rgba(255,255,255,0.5), 0 0 12px 6px rgba(255,255,255,0.2)',
          }}
        />
        <OrderPlacementDialog
          cell={dialogCell}
          initialSide={dialogSide}
          onClose={() => setDialogCell(null)}
          onPlaced={() => setDialogCell(null)}
        />
        {/* Форс-модалка при попытке BUY на истёкшей подписке */}
        <RenewalModal
          forceOpen={showExpiredBlocker}
          onClose={() => setShowExpiredBlocker(false)}
        />

        {!chartApi && (
          <div
            className="pointer-events-none absolute top-0 z-20 bg-[#64748b]"
            style={{ width: 1, left: `${linePosition}%`, height: 'calc(100% - 28px)' }}
            aria-hidden
          />
        )}
      </div>
    </ChartApiProvider>
  )
})
